import React, { useContext } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { SubscriptionContext } from "redux/contexts/subscription"
import { selectSettingsById } from "redux/features/settings/settingsSlice"
import { openDialog } from "redux/features/dialog/dialogSlice"
import { formatDate, formatMoney } from "helpers/formatters"
import {
  isIssuesCreate,
  isPaymentRequestsCreate,
  isPaymentSchedulesSign,
  isFreshFinance,
} from "helpers/features"
import {
  PAYMENT_PAY_MODAL,
  SEPA_PAYMENT_PAY_MODAL,
  PAYMENT_SCHEDULE_PROMISE_ACTION,
  PAYMENT_SCHEDULE_SIGN_ACTION,
  SUBSCRIPTION_CREATE_MODAL,
} from "config"
import Doughnut from "components/ui/Doughnut"
import Title from "components/Debt/Title"
import Manager from "components/Debt/Manager"
import Summary from "components/Debt/Summary"
import Information from "components/ui/InfoList"
import Card from "components/ui/CardAccordion"
import {
  CollapsedAside,
  Balance,
  CollapsedActions,
  CollapsedRedButton,
  CollapsedPrimaryButton,
  Container,
  Aside,
  Content,
  Info,
  ActionList,
  ActionListItem,
  RedButton,
  LightButton,
  PrimaryButton,
} from "components/Debt/elements"
import { DebtType, SettingsType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  item: DebtType
}

const DebtDraftPaymentSchedule = ({ item }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { needSubscription } = useContext(SubscriptionContext)

  const settings = useAppSelector(
    (state) => selectSettingsById(state, 0) as SettingsType,
  )

  const {
    id,
    start_date,
    total_paid_percent,
    unpaid_total_sum,
    payment_schedule,
    is_white,
    creditor,
    manager,
    company,
  } = item
  const action = is_white
    ? PAYMENT_SCHEDULE_PROMISE_ACTION
    : PAYMENT_SCHEDULE_SIGN_ACTION

  const general = [
    {
      label: t("features.debt.list.start_date.title"),
      value: formatDate(start_date),
      visible: true,
    },
    {
      label: t("features.debt.list.creditor.title"),
      value: creditor?.name,
      visible: true,
    },
    {
      label: t("features.debt.list.company.title"),
      value: company?.legal_name,
      visible: isFreshFinance(),
    },
    {
      label: t("features.debt.list.next_payment.title", {
        date: formatDate(
          payment_schedule?.next_payment_schedule_invoice?.due_date,
        ),
      }),
      value: formatMoney(
        payment_schedule?.next_payment_schedule_invoice?.unpaid_sum,
      ),
      visible: true,
    },
    {
      label: t("features.debt.list.first_payment.title", {
        date: formatDate(
          payment_schedule?.first_payment_schedule_invoice?.due_date,
        ),
      }),
      value: formatMoney(
        payment_schedule?.first_payment_schedule_invoice?.unpaid_sum,
      ),
      visible: true,
    },
  ].filter((item) => item.value && item.visible)

  const handlePaymentScheduleSign = () => {
    if (payment_schedule) {
      navigate(
        `/debts/${id}/payment_schedules/${payment_schedule.id}/${action}`,
      )
    }
  }

  const handlePaymentCreate = (
    event: React.SyntheticEvent,
    amount: number | undefined,
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    dispatch(
      openDialog({
        type: isPaymentRequestsCreate(settings?.features)
          ? PAYMENT_PAY_MODAL
          : SEPA_PAYMENT_PAY_MODAL,
        content: {
          debtId: id,
          paymentScheduleId: payment_schedule?.id,
          amount: amount,
        },
      }),
    )
  }

  const openSubscriptionCreateModal = () => {
    dispatch(openDialog({ type: SUBSCRIPTION_CREATE_MODAL }))
  }

  const handleClick = () => {
    if (needSubscription) {
      openSubscriptionCreateModal()
    } else {
      handlePaymentScheduleSign()
    }
  }

  const handleIssueCreate = () => {
    navigate(`/debts/${id}/issues/new`)
  }

  return (
    <Card
      collapsed={
        <>
          <CollapsedAside>
            <Title compact debt={item} />
            <Balance variant="h4">{formatMoney(unpaid_total_sum)}</Balance>
          </CollapsedAside>
          <CollapsedActions>
            {isPaymentRequestsCreate(settings?.features) && (
              <CollapsedPrimaryButton
                onClick={(event) =>
                  handlePaymentCreate(event, unpaid_total_sum)
                }
              >
                {t("features.debt.actions.payment.fully.title")}
              </CollapsedPrimaryButton>
            )}
            {isPaymentSchedulesSign(settings?.features) && (
              <CollapsedRedButton onClick={handleClick}>
                {t(`features.debt.actions.payment_schedule.${action}.title`)}
              </CollapsedRedButton>
            )}
          </CollapsedActions>
        </>
      }
      expanded={
        <Container>
          <Aside>
            <Doughnut
              top={t("features.debt.doughnut.remain.title")}
              amount={formatMoney(unpaid_total_sum)}
              value={total_paid_percent}
            />
            <Summary item={item} />
          </Aside>
          <Content>
            <Info>
              <Title debt={item} />
              <Manager dense manager={manager} />
              <Information dense items={general} />
            </Info>
            <ActionList>
              {isPaymentRequestsCreate(settings?.features) && (
                <ActionListItem>
                  <PrimaryButton
                    onClick={(event) =>
                      handlePaymentCreate(event, unpaid_total_sum)
                    }
                  >
                    {t("features.debt.actions.payment.fully.title")}
                  </PrimaryButton>
                </ActionListItem>
              )}
              {isPaymentSchedulesSign(settings?.features) && (
                <ActionListItem>
                  <RedButton onClick={handleClick}>
                    {t(
                      `features.debt.actions.payment_schedule.${action}.title`,
                    )}
                  </RedButton>
                </ActionListItem>
              )}
              {isIssuesCreate(settings?.features) && (
                <ActionListItem>
                  <LightButton onClick={handleIssueCreate}>
                    {t("features.debt.actions.issue.create.title")}
                  </LightButton>
                </ActionListItem>
              )}
            </ActionList>
          </Content>
        </Container>
      }
    />
  )
}

export default DebtDraftPaymentSchedule
